import { useEffect, useState, useCallback } from 'react';
import api from '../services/ApiService';

type TrunkStatus = {
  id: number;
  trunk_name: string;
  display_name: string;
  sip_host: string;
  sip_port: number;
  transport_protocol: string;
  is_active: number;
  tenant_id: number | null;
  pjsip_status: string;
  reachable: boolean | null;
  contact_uri: string | null;
  rtt_ms: number | null;
  last_checked: string | null;
};

const PJSIP_COLORS: Record<string, string> = {
  Avail:        '#10b981',
  NonQualify:   '#f59e0b',
  NonQual:      '#f59e0b',
  Unknown:      '#64748b',
  Unavail:      '#ef4444',
};

function PjsipBadge({ status }: { status: string }) {
  const color = PJSIP_COLORS[status] || '#64748b';
  const label = status === 'Avail' ? '✓ Reachable' : (status === 'NonQualify' || status === 'NonQual') ? 'No Qualify' : status;
  return (
    <span style={{
      background: `${color}18`, border: `1px solid ${color}40`,
      color, borderRadius: 4, padding: '3px 9px', fontSize: 11, fontWeight: 700,
    }}>{label}</span>
  );
}

function ProtoBadge({ proto }: { proto: string }) {
  const colors: Record<string, string> = { tls: '#10b981', tcp: '#3b82f6', udp: '#64748b' };
  const c = colors[proto?.toLowerCase()] || '#64748b';
  return (
    <span style={{ background: `${c}18`, color: c, borderRadius: 4, padding: '2px 7px', fontSize: 10, fontWeight: 700, textTransform: 'uppercase' }}>
      {proto || 'UDP'}
    </span>
  );
}

export default function TrunkStatusPage() {
  const [trunks, setTrunks]       = useState<TrunkStatus[]>([]);
  const [loading, setLoading]     = useState(false);
  const [qualifying, setQualifying] = useState<Record<number, boolean>>({});
  const [qualifyingAll, setQualifyingAll] = useState(false);
  const [error, setError]         = useState<string | null>(null);
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await (api as any).getTrunkStatus();
      setTrunks(Array.isArray(data) ? data : []);
      setLastRefresh(new Date());
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Failed to load trunk status');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function qualifyTrunk(t: TrunkStatus) {
    setQualifying(q => ({ ...q, [t.id]: true }));
    try {
      const aor = `${t.trunk_name}-aor`;
      const result = await (api as any).qualifyAor(aor);
      setTrunks(prev => prev.map(x => x.id === t.id ? {
        ...x,
        pjsip_status: result.pjsip_status,
        reachable:    result.reachable,
        rtt_ms:       result.rtt_ms,
        last_checked: new Date().toISOString(),
      } : x));
    } catch (e: any) {
      setError(`Qualify failed for ${t.trunk_name}: ${e?.response?.data?.error || e.message}`);
    } finally {
      setQualifying(q => ({ ...q, [t.id]: false }));
    }
  }

  async function qualifyAll() {
    setQualifyingAll(true);
    try {
      for (const t of trunks) {
        await qualifyTrunk(t);
      }
    } finally {
      setQualifyingAll(false);
    }
  }

  const reachable   = trunks.filter(t => t.reachable === true).length;
  const unreachable = trunks.filter(t => t.reachable === false).length;
  const unknown     = trunks.filter(t => t.reachable === null).length;

  return (
    <div style={{ padding: '24px' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 20 }}>
        <div>
          <div style={{ fontSize: 20, fontWeight: 800, color: '#f1f5f9' }}>Trunk Status</div>
          <div style={{ fontSize: 13, color: '#64748b', marginTop: 2 }}>
            PJSIP reachability — OPTIONS ping runs on Telephony Engine
          </div>
        </div>
        <div style={{ flex: 1 }} />
        <button onClick={load} disabled={loading} style={{
          background: 'rgba(30,41,59,0.8)', border: '1px solid rgba(100,116,139,0.3)',
          color: '#94a3b8', borderRadius: 8, padding: '8px 14px', fontSize: 13, cursor: 'pointer',
        }}>
          {loading ? '…' : '↻ Refresh'}
        </button>
        <button onClick={qualifyAll} disabled={qualifyingAll || trunks.length === 0} style={{
          background: qualifyingAll ? '#1d4ed8' : 'linear-gradient(135deg,#8b5cf6,#6366f1)',
          border: 'none', color: 'white', borderRadius: 8,
          padding: '8px 16px', fontSize: 13, fontWeight: 700, cursor: 'pointer',
        }}>
          {qualifyingAll ? 'Pinging…' : '⚡ OPTIONS Ping All'}
        </button>
      </div>

      {error && (
        <div style={{ background: 'rgba(239,68,68,0.12)', border: '1px solid #ef444440', color: '#fca5a5',
          borderRadius: 8, padding: '10px 14px', fontSize: 13, marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
          {error}
          <span style={{ cursor: 'pointer' }} onClick={() => setError(null)}>✕</span>
        </div>
      )}

      {/* Stats */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20 }}>
        {[
          { label: 'Total Trunks',  value: trunks.length, color: '#8b5cf6' },
          { label: 'Reachable',     value: reachable,     color: '#10b981' },
          { label: 'Unreachable',   value: unreachable,   color: '#ef4444' },
          { label: 'Unknown',       value: unknown,       color: '#64748b' },
        ].map(s => (
          <div key={s.label} style={{
            background: 'rgba(30,41,59,0.7)', border: `1px solid ${s.color}25`,
            borderRadius: 12, padding: '14px 20px', flex: 1,
          }}>
            <div style={{ fontSize: 26, fontWeight: 800, color: s.color }}>{s.value}</div>
            <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>{s.label}</div>
          </div>
        ))}
        {lastRefresh && (
          <div style={{
            background: 'rgba(30,41,59,0.5)', border: '1px solid rgba(100,116,139,0.15)',
            borderRadius: 12, padding: '14px 20px', flex: 1.5,
            display: 'flex', flexDirection: 'column', justifyContent: 'center',
          }}>
            <div style={{ fontSize: 12, color: '#64748b' }}>Status as of</div>
            <div style={{ fontSize: 13, color: '#e2e8f0', fontWeight: 600, marginTop: 2 }}>
              {lastRefresh.toLocaleTimeString()}
            </div>
          </div>
        )}
      </div>

      {/* Table */}
      <div style={{ background: 'rgba(15,23,42,0.6)', border: '1px solid rgba(100,116,139,0.15)', borderRadius: 12, overflow: 'hidden' }}>
        {trunks.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#475569', fontSize: 14 }}>
            {loading ? 'Loading trunk status…' : 'No trunks configured.'}
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: 'rgba(30,41,59,0.8)', borderBottom: '1px solid rgba(100,116,139,0.15)' }}>
                {['Trunk', 'Proto', 'Host', 'Port', 'PJSIP Status', 'RTT', 'Contact URI', 'Last Ping', 'Action'].map(h => (
                  <th key={h} style={{ padding: '10px 12px', textAlign: 'left', color: '#64748b', fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {trunks.map((t, i) => (
                <tr key={t.id} style={{
                  borderBottom: '1px solid rgba(100,116,139,0.08)',
                  background: i % 2 === 0 ? 'transparent' : 'rgba(30,41,59,0.15)',
                }}>
                  <td style={{ padding: '10px 12px' }}>
                    <div style={{ fontWeight: 700, color: '#e2e8f0' }}>{t.trunk_name}</div>
                    {t.display_name && t.display_name !== t.trunk_name && (
                      <div style={{ fontSize: 11, color: '#64748b' }}>{t.display_name}</div>
                    )}
                    {!t.tenant_id && (
                      <div style={{ fontSize: 10, color: '#a78bfa', fontWeight: 600, marginTop: 1 }}>Platform</div>
                    )}
                  </td>
                  <td style={{ padding: '10px 12px' }}><ProtoBadge proto={t.transport_protocol} /></td>
                  <td style={{ padding: '10px 12px', color: '#cbd5e1', fontFamily: 'monospace', fontSize: 12 }}>{t.sip_host}</td>
                  <td style={{ padding: '10px 12px', color: '#94a3b8', fontFamily: 'monospace' }}>{t.sip_port}</td>
                  <td style={{ padding: '10px 12px' }}>
                    {qualifying[t.id]
                      ? <span style={{ color: '#f59e0b', fontSize: 11 }}>⟳ Pinging…</span>
                      : <PjsipBadge status={t.pjsip_status} />}
                  </td>
                  <td style={{ padding: '10px 12px', color: t.rtt_ms ? '#10b981' : '#475569', fontFamily: 'monospace', fontSize: 12 }}>
                    {t.rtt_ms != null ? `${t.rtt_ms.toFixed(1)} ms` : '—'}
                  </td>
                  <td style={{ padding: '10px 12px', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    <span style={{ fontSize: 11, color: '#64748b', fontFamily: 'monospace' }}>
                      {t.contact_uri || '—'}
                    </span>
                  </td>
                  <td style={{ padding: '10px 12px', color: '#475569', fontSize: 11 }}>
                    {t.last_checked ? new Date(t.last_checked).toLocaleTimeString() : '—'}
                  </td>
                  <td style={{ padding: '10px 12px' }}>
                    <button
                      onClick={() => qualifyTrunk(t)}
                      disabled={!!qualifying[t.id]}
                      style={{
                        background: 'rgba(139,92,246,0.15)', border: '1px solid rgba(139,92,246,0.3)',
                        color: '#c4b5fd', borderRadius: 6, padding: '5px 12px',
                        fontSize: 11, fontWeight: 600, cursor: 'pointer',
                      }}>
                      {qualifying[t.id] ? '…' : '⚡ Ping'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      <div style={{ marginTop: 10, fontSize: 11, color: '#334155' }}>
        OPTIONS pings are sent from the Telephony Engine. NonQualify = qualify_frequency=0 on this AOR.
      </div>
    </div>
  );
}
